import './NotificationDisplay.scss';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useState, useRef, useEffect, useContext } from 'react';
import Moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { useClickOutside } from '../Utilities/HelperFunctions';
import NotificationService from '../../Services/NotificationService';
import NotificationContext from '../../Context/NotificationContext';
import ClientService from '../../Services/ClientService';

const gT = (t, key) => t(`notification-display${key}`);

/* eslint-disable react/prop-types */
function NotificationItem({ notification, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`notification-item ${
        notification.read === false
          ? 'unread-notification'
          : 'read-notification'
      }`}
    >
      <div className="text-column">
        <p className="message-text">{notification.message}</p>
        <p className="datetime-text">
          {Moment(notification.dateCreated).format('YYYY-MM-DD HH:mm')}
        </p>
      </div>
      <div className="icon-column">
        <div className="unread-dot" />
      </div>
    </button>
  );
}

function ActionButton({ text, onClick, disabled }) {
  return (
    <button
      type="button"
      className="notifications-action-button"
      onClick={onClick}
      disabled={disabled}
    >
      {text}
    </button>
  );
}

export default function NotificationDisplay({ isImpersonating }) {
  const { t } = useTranslation('app');
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const contentRef = useRef();
  useClickOutside(contentRef, () => {
    setOpen(false);
  });
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [notifications, setNotifications] = useContext(NotificationContext);

  const markNotificationAsRead = async (notificationId) => {
    await NotificationService.markSingleWebNotificationAsRead(notificationId)
      .then(() => {
        const newNotificationList = [...notifications];
        const index = newNotificationList.findIndex(
          (notification) => notification.id === notificationId
        );
        if (index !== -1) {
          newNotificationList[index].read = true;
          setNotifications(newNotificationList);
        }
      })
      .catch(() => {});
  };

  const markAllAsRead = async () => {
    await NotificationService.markWebNotificationsAsRead(
      notifications[0].dateCreated
    )
      .then(() => {
        const newNotificationList = [...notifications];
        for (let i = 0; i < newNotificationList.length; i += 1) {
          newNotificationList[i].read = true;
        }
        setNotifications(newNotificationList);
      })
      .catch(() => {});
  };

  useEffect(() => {
    setHasUnreadNotifications(
      notifications.some((notification) => notification.read === false)
    );
  }, [notifications]);

  async function OnNotificationClick(notification) {
    if (notification.clientId) {
      await ClientService.userCustomerHasAccessToClient(
        notification.clientId
      ).then((response) => {
        if (response.data === true) {
          let url = `/app/companyinfo/${notification.clientId}`;
          if (notification.messageKey === 4) {
            url += '/equity';
          } else if (notification.messageKey === 5) {
            url += '/accounts';
          } else if (
            notifications.messageKey === 6 ||
            notifications.messageKey === 7
          ) {
            url += '/regulatory';
          } else {
            url += '/score';
          }
          history.push(url);
          return true;
        }
        return false;
      });

      return true;
    }
    return false;
  }

  return (
    <div className="notification-display-wrapper" ref={contentRef}>
      <button
        type="button"
        className={`notifications-button ${
          open && 'notifications-button-clicked'
        }`}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <FontAwesomeIcon className="bell-icon" icon={faBell} />
        {hasUnreadNotifications && <p className="unread-indicator">!</p>}
      </button>
      {open && (
        <div
          className={`content-window ${
            open ? 'content-open' : 'content-hidden'
          }`}
          style={{ top: `${isImpersonating ? '120px' : '72px'}` }}
        >
          <div className="action-row">
            <ActionButton
              text={gT(t, '.mark-all-as-read')}
              onClick={markAllAsRead}
              disabled={!hasUnreadNotifications}
            />
          </div>
          <div className="notification-list">
            {notifications &&
              notifications.map((notification) => {
                return (
                  <NotificationItem
                    onClick={async () => {
                      const redirected = await OnNotificationClick(
                        notification
                      );
                      if (redirected) setOpen(false);
                      if (notification.read === false) {
                        markNotificationAsRead(notification.id);
                      }
                    }}
                    notification={notification}
                    key={notification.id}
                  />
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
